// SPDX-License-Identifier: GPL-3.0-only
// AI2 v14/v15/v17/v19/v20 layout: pinned OpenSaga ai_sys_stubs.cpp AISysLoadPaths/Areas/
// Locators/Creatures. Formation/reset: ai/game/creature.cpp:InitAICreatures,
// ResetAICreature, ResetAICreatures. No inferred scene-mesh spawn locations.
export function parseAI2(source) {
  const bytes = source instanceof ArrayBuffer ? new Uint8Array(source) : source;
  if (!(bytes instanceof Uint8Array) || bytes.length < 8 || bytes.length > 64 * 1024 * 1024) throw new Error('Invalid AI2 size');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let at = 0, section = 'header';
  const take = n => { if (!Number.isSafeInteger(n) || n < 0 || at + n > bytes.length) throw new Error(`AI2 out of bounds at ${at}`); const o = at; at += n; return o; };
  const u8 = () => view.getUint8(take(1)), i8 = () => view.getInt8(take(1)), u16 = () => view.getUint16(take(2), true), i16 = () => view.getInt16(take(2), true), i32 = () => view.getInt32(take(4), true), u32 = () => view.getUint32(take(4), true);
  const f32 = () => { const address = take(4), v = view.getFloat32(address, true); if (!Number.isFinite(v)) throw new Error(`Nonfinite AI2 field in ${section} at 0x${address.toString(16)}`); return v; };
  const vec = () => [f32(), f32(), f32()];
  const count = (n, max = 10000) => { if (!Number.isInteger(n) || n < 0 || n > max) throw new Error('Invalid AI2 count'); return n; };
  const text = n => { const o = take(n), raw = bytes.subarray(o, o + n), end = raw.indexOf(0); return String.fromCharCode(...raw.subarray(0, end < 0 ? raw.length : end)); };
  const namedRef = () => i32() ? text(16).toLowerCase() : null;
  const pathRef = () => ({ pathIndex: u8(), direction: u8(), connectionIndex: u16() });
  const version = i32(); if (![14, 15, 17, 19, 20].includes(version)) throw new Error(`Unsupported AI2 version ${version}; proven PC v14/v15/v17/v19/v20 are implemented`);
  const paths = [], pathCount = count(i32(), 255);
  for (let p = 0; p < pathCount; p++) {
    const name = text(16), nodeCount = count(u8(), 255), flags = u8(), connectionCount = count(i16(), 32767), connections = [], nodes = [];
    // PC endpoint indices are the leading bytes (BrickBench AI2Loader and
    // actual node references); the two following words are capability flags.
    // The reconstructed Android loader's provisional node_a aliases differ.
    for (let c = 0; c < connectionCount; c++) {
      section = `path ${p} connection ${c}`;
      connections.push({ nodeA: u8(), nodeB: u8(), flagsA: i32(), flagsB: i32(), angle: u16(), gameFlags: u16(), width: f32(), cost: f32() });
    }
    for (let j = 0; j < nodeCount; j++) {
      section = `path ${p} node ${j}`;
      const nodeName = text(count(i32(), 1024)), position = vec(), radius = f32(), minHeight = f32(), maxHeight = f32();
      const links = count(u8(), 255), nodeFlags = u8(), reserved = u8(), runtimeFlags = u8(), pathFlags = u16(), serializedSpecialRouteIndex = u8();
      // v19 introduced this byte's special-route meaning. Earlier versions
      // still serialize a byte, but native loader ignores it and assigns255.
      const specialRouteIndex = version >= 19 ? serializedSpecialRouteIndex : 255;
      const specialName = text(count(i8(), 127)), specialPosition = specialName ? vec() : null, connectionIndices = [];
      if (connectionCount && links) { for (let k = 0; k < links; k++) connectionIndices.push(u16()); if (links & 1) u16(); }
      const routeMembershipMask = u16(), routeBoundaryMask = u16();
      if (connectionIndices.some(index => index >= connectionCount)) throw new Error('AI2 node references invalid connection');
      nodes.push({ name: nodeName, position, radius, minHeight, maxHeight, flags: nodeFlags, reserved, runtimeFlags, pathFlags, specialRouteIndex, serializedSpecialRouteIndex, specialName, specialPosition, connectionIndices, routeMembershipMask, routeBoundaryMask });
    }
    const routeMatrix = Array.from(bytes.subarray(take(nodeCount * nodeCount), at));
    const routes = [];
    for (let r = 0, total = count(u8(), 255); r < total; r++) {
      const name = text(count(i8(), 127)); let routeCount = 0, exitCount = 0;
      if (name) { routeCount = u8(); exitCount = u8(); take(2); if (nodeCount && routeCount) take(nodeCount + routeCount + routeCount * routeCount + exitCount); }
      const characters = []; for (let j = 0, total = count(i8(), 127); j < total; j++) characters.push(text(count(i8(), 127)));
      routes.push({ name, routeCount, exitCount, characters });
    }
    const specialRoutes = []; for (let j = 0, total = version > 18 ? count(u8(), 255) : 0; j < total; j++) specialRoutes.push({ nodeIndex: u8(), specialRouteIndex: i16() });
    if (connections.some(c => c.nodeA < 0 || c.nodeA >= nodeCount || c.nodeB < 0 || c.nodeB >= nodeCount)) throw new Error('AI2 connection references invalid node');
    paths.push({ name, flags, connections, nodes, routeMatrix, routes, specialRoutes });
  }
  const pathSpecialRoutes = [];
  for (let i = 0, total = pathCount && version > 18 ? count(i16(), 32767) : 0; i < total; i++) {
    const indices = []; for (let j = 0, n = count(u8(), 255); j < n; j++) indices.push(u8());
    if (indices.some(v => v >= paths.length)) throw new Error('AI2 special route references invalid path');
    pathSpecialRoutes.push(indices);
  }
  const areas = [];
  section = 'areas';
  for (let i = 0, total = count(i32()); i < total; i++) {
    const name = text(16).toLowerCase(), position = vec(), extents = vec(), rotation = i16(), serializedGameFlags = u8(), reserved = u8();
    // v20 is the first version where the first trailing area byte initializes
    // game_flags. AISysLoadAreas ignores BOTH bytes for v19 and earlier.
    areas.push({ name, position, extents, rotation, gameFlags: version > 19 ? serializedGameFlags : 0, serializedGameFlags, reserved });
  }
  if (!pathCount) return { version, paths, pathSpecialRoutes, areas, locators: [], locatorSets: [], creatures: [], consumedBytes: at, remainingBytes: bytes.length - at,
    limitations: ['Original loader skips locator/creature sections when no AI path system exists.'] };
  const locators = [];
  section = 'locators';
  for (let i = 0, total = count(i32()); i < total; i++) {
    const name = text(16).toLowerCase(), position = vec(), angle = i16(), path = pathRef(), distance = f32(), width = f32(), flags = version > 14 ? i32() : 0;
    locators.push({ name, position, heading: (angle & 65535) * Math.PI / 32768, angle, path, distance, width, flags });
  }
  const locatorSets = [];
  for (let i = 0, total = version > 17 ? count(i32()) : 0; i < total; i++) {
    const name = text(16).toLowerCase(), members = [];
    for (let j = 0, n = count(i32(), 127); j < n; j++) members.push(u8());
    if (members.some(v => v >= locators.length)) throw new Error('AI2 set references invalid locator');
    locatorSets.push({ name, members });
  }
  const creatures = [];
  for (let i = 0, total = count(i32(), 255); i < total; i++) {
    section = `creature ${i}`;
    const address = at, name = text(16), script = text(16) || 'default', character = text(32), position = vec(), angle = i16();
    const set = version > 15 ? u8() : 0, spawnCount = u8(), countAcross = u8(), activeMask = u32(), xSpacing = f32(), zSpacing = f32(), flags = i32(), path = pathRef();
    const scriptParams = [f32(), f32(), f32(), f32()], area = namedRef(), locator = namedRef(), respawnLocator = version > 16 ? namedRef() : null;
    const activationDifficulty = u8(), minRespawnCount = i8(), maxRespawnCount = i8(), activateType = u8(), minRespawnTime = f32(), maxRespawnTime = f32(), startStagger = f32();
    const activateArea = activateType === 1 ? text(16).toLowerCase() : null;
    const viewDistance = f32(), hearDistance = f32(), maxViewHeight = f32(), minViewHeight = f32(), reserved = i32();
    if (!character || path.pathIndex >= paths.length) throw new Error('Invalid AI2 creature character/path');
    creatures.push({ index: i, address, name, script: script.toLowerCase(), character: character.toLowerCase(), position, angle,
      heading: (angle & 65535) * Math.PI / 32768, set, count: spawnCount, countAcross, activeMask, xSpacing, zSpacing, flags, path,
      scriptParams, area, locator, respawnLocator, activationDifficulty, minRespawnCount, maxRespawnCount, activateType,
      minRespawnTime, maxRespawnTime, startStagger, activateArea, viewDistance, hearDistance, maxViewHeight, minViewHeight, reserved });
  }
  return { version, paths, pathSpecialRoutes, areas, locators, locatorSets, creatures, consumedBytes: at,
    remainingBytes: bytes.length - at, limitations: ['Antinodes and game-specific tail are not decoded.', 'AI metadata alone does not initialize navigation, scripts or physical creature state.'] };
}

export function expandCreatureSpawns(ai, { difficulty, lowEnd = false, missionMode = false, netClient = false } = {}) {
  if (!Number.isInteger(difficulty) || difficulty < 0 || difficulty > 255) throw new Error('Explicit source game difficulty is required');
  if (missionMode || netClient) return [];
  const spawns = [];
  for (const creature of ai.creatures) {
    if (lowEnd && (creature.flags & 0x20)) continue;
    const grouped = creature.count > 1 && creature.countAcross > 0 && creature.startStagger === 0 && (creature.activeMask & 1);
    let packedMember = 0;
    for (let member = 0; member < creature.count; member++) {
      if (member >= 32 || !(creature.activeMask & (1 << member))) continue;
      const column = grouped ? packedMember % creature.countAcross : 0, row = grouped ? Math.floor(packedMember / creature.countAcross) : 0;
      const x = Math.floor((column + 1) / 2) * creature.xSpacing * (column & 1 ? -1 : 1), z = -creature.zSpacing * row;
      const units = creature.angle & 65535, angle = (units >> 1) * (2 * Math.PI / 32768), sine = Math.fround(Math.sin(angle)), cosine = Math.fround(Math.cos(angle));
      const position = [creature.position[0] + x * cosine + z * sine, creature.position[1], creature.position[2] + z * cosine - x * sine];
      const runtimeState = creature.activateType !== 0 ? 'ready' : difficulty < creature.activationDifficulty ? 'disabled'
        : creature.count > 1 && creature.startStagger > 0 && packedMember !== 0 ? 'staggered' : 'active';
      spawns.push({ ...creature, id: `ai:${creature.index}:${member}`, member, groupMember: packedMember++, position,
        origin: position.slice(), runtimeState, spawnDelay: runtimeState === 'staggered' ? (packedMember - 1) * creature.startStagger : 0,
        eligibleDamage: null, reason: runtimeState === 'ready' ? 'Original activation/trigger/script condition must resolve before reset' : null });
    }
  }
  return spawns;
}

// CheckPosAIArea, legoapi/ai/core/gameai.cpp:45. Extents are authored local
// half-width, vertical height above origin, half-depth, not a world AABB.
export function insideAITrigger(area, point, tolerance = 0) {
  if (!area || !point || point.length !== 3 || !point.every(Number.isFinite) || !Number.isFinite(tolerance) || tolerance < 0) throw new Error('Invalid AI trigger query');
  const angle = -area.rotation * Math.PI / 32768, dx = point[0] - area.position[0], dz = point[2] - area.position[2];
  const x = dx * Math.cos(angle) + dz * Math.sin(angle), z = dz * Math.cos(angle) - dx * Math.sin(angle), y = point[1] - area.position[1];
  return x + tolerance >= -area.extents[0] && x - tolerance <= area.extents[0] &&
    y + tolerance >= -0.1 && y - tolerance <= area.extents[1] && z + tolerance >= -area.extents[2] && z - tolerance <= area.extents[2];
}

function scriptTree(text) {
  if (typeof text !== 'string' || text.length > 4 * 1024 * 1024) throw new Error('Invalid AI script text');
  const root = { header: [], children: [] }, stack = [root];
  for (const [lineIndex, line] of text.split(/\r?\n/).entries()) {
    let quoted = false, clean = '';
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') quoted = !quoted;
      if (!quoted && (line.slice(i, i + 2) === '//' || line[i] === ';')) break;
      clean += line[i];
    }
    if (!clean.trim()) continue;
    const tokens = [...clean.matchAll(/"([^"]*)"|([{}])|([^\s{}]+)/g)].map(m => m[1] ?? m[2] ?? m[3]);
    let header = [];
    for (const token of tokens) {
      if (token === '{') { const node = { header, children: [], line: lineIndex + 1 }; stack.at(-1).children.push(node); stack.push(node); header = []; }
      else if (token === '}') {
        if (header.length || stack.length === 1) throw new Error(`Invalid AI script block at line ${lineIndex + 1}`);
        stack.pop();
      } else header.push(token);
    }
    if (header.length) stack.at(-1).children.push({ header, children: null, line: lineIndex + 1 });
  }
  if (stack.length !== 1) throw new Error('Unterminated AI script block');
  return root;
}
function conditions(block) {
  const result = [];
  for (const line of block?.children ?? []) {
    const a = line.header;
    if (a[0]?.toLowerCase() !== 'if') throw new Error(`Unsupported AI condition at line ${line.line}`);
    const operator = a.findIndex(v => ['==', '!=', '<', '>', '<=', '>='].includes(v));
    if (operator < 2 || operator + 1 >= a.length) throw new Error('Invalid AI comparison');
    const suffix = a.slice(operator + 2), and = suffix[0]?.toLowerCase() === 'and';
    const target = suffix[0]?.toLowerCase() === 'goto' ? suffix[1]?.toLowerCase() : null;
    const number = Number(a[operator + 1]);
    result.push({ name: a[1].toLowerCase(), args: a.slice(2, operator), operator: a[operator], value: Number.isFinite(number) ? number : null,
      rawValue: a[operator + 1], and, target, unsupportedSuffix: !and && !target && suffix.length > 0, line: line.line });
  }
  return result;
}
function references(block) {
  return block.children.filter(n => n.header[0]?.toLowerCase() === 'referencescript').map(node => {
    const props = {};
    for (const item of node.children.filter(n => !n.children)) { const [k, ...v] = item.header.join(' ').split('='); props[k.toLowerCase()] = v.join('=').trim().toLowerCase(); }
    return { script: props.script, source: props.source, returnState: props.returnstate,
      conditions: conditions(node.children.find(n => n.header[0]?.toLowerCase() === 'conditions')) };
  });
}
export function parseEnemyScript(text, { name = 'anonymous' } = {}) {
  const tree = scriptTree(text), states = new Map();
  for (const state of tree.children.filter(n => n.header[0]?.toLowerCase() === 'state')) {
    const id = state.header[1]?.toLowerCase(); if (!id || states.has(id)) throw new Error('Duplicate/invalid AI state');
    const actionBlock = state.children.find(n => n.header[0]?.toLowerCase() === 'actions');
    const actions = (actionBlock?.children ?? []).map(n => ({ name: n.header[0].toLowerCase(), args: n.header.slice(1), line: n.line }));
    states.set(id, { name: id, conditions: conditions(state.children.find(n => n.header[0]?.toLowerCase() === 'conditions')), actions, references: references(state) });
  }
  if (!states.size) throw new Error('AI script has no states');
  for (const state of states.values()) for (const condition of state.conditions) if (condition.target && !states.has(condition.target)) throw new Error(`AI condition references unknown state ${condition.target}`);
  return { name: name.toLowerCase(), states, baseState: states.has('base') ? 'base' : states.keys().next().value, references: references(tree) };
}

// Subset of AIScriptProcess: timer -> pending state -> sequential actions ->
// reference entry checks -> ordered AND condition groups. Physical actions
// are coroutines: binding true means complete, false means still executing,
// undefined means unresolved. No unknown action is silently completed.
export class EnemyScriptRuntime {
  constructor(script, { scripts = new Map(), condition = () => undefined, action = () => undefined, messages = new Map(), maxActions = 64 } = {}) {
    this.script = script; this.scripts = scripts; this.condition = condition; this.action = action; this.messages = messages;
    this.maxActions = maxActions; this.timer = 0; this.stack = []; this.nextState = null; this.setState(script.baseState);
  }
  setState(name) {
    const state = this.script.states.get(name.toLowerCase()); if (!state) throw new Error(`Unknown original AI state ${name}`);
    this.state = state; this.actionIndex = 0; this.firstTime = true; this.nextState = null;
  }
  transition(list, context, blocked) {
    // AIScriptProcessStateConditions starts condition_value0 for each list.
    // PreviousResult has a NULL evaluator, retaining the last evaluated
    // condition's numeric value; it is unrelated to action completion.
    let chain = true, previousValue = 0;
    for (const condition of list) {
      if (!chain) { if (!condition.and) chain = true; continue; }
      let value;
      if (condition.name === 'alwaystrue') value = 1;
      else if (condition.name === 'timer') value = this.timer;
      else if (condition.name === 'message') value = this.messages.get(condition.args[0]?.toLowerCase()) ?? 0;
      else if (condition.name === 'previousresult') value = previousValue;
      else value = this.condition(condition.name, condition.args, context, this);
      previousValue = value;
      let passed = false;
      if (value === undefined || value === null || condition.value === null || condition.unsupportedSuffix) {
        blocked.push({ kind: 'condition', name: condition.name, args: condition.args, line: condition.line });
      } else {
        value = typeof value === 'boolean' ? Number(value) : value;
        if (!Number.isFinite(value)) throw new Error('Nonfinite AI condition result');
        const target = condition.value;
        passed = condition.operator === '==' ? value === target : condition.operator === '!=' ? value !== target
          : condition.operator === '<' ? value < target : condition.operator === '>' ? value > target
          : condition.operator === '<=' ? value <= target : value >= target;
      }
      chain &&= passed;
      if (!condition.and) { if (chain) return condition; chain = true; }
    }
    return null;
  }
  tick(dt, context = {}) {
    if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Invalid AI timestep');
    this.timer += dt;
    const blocked = [], commands = [];
    if (this.nextState) this.setState(this.nextState);
    else if (this.stack.length && this.state.name === this.script.baseState) {
      const prior = this.stack.pop(); this.script = prior.script; this.setState(prior.returnState);
    }
    let actions = 0;
    while (this.actionIndex < this.state.actions.length) {
      if (++actions > this.maxActions) { blocked.push({ kind: 'budget', name: 'actions' }); break; }
      const a = this.state.actions[this.actionIndex], firstTime = this.firstTime;
      this.firstTime = false; let complete;
      if (a.name === 'setstate') { if (firstTime) { if (!a.args[0]) throw new Error('SetState missing target'); this.nextState = a.args[0].toLowerCase(); } complete = false; }
      else if (a.name === 'resettimer') {
        if (a.args.length) { blocked.push({ kind: 'action', name: a.name, reason: 'Parameterized/random timer unresolved' }); break; }
        if (firstTime) this.timer = 0; complete = true;
      } else if (a.name === 'setmessage') {
        const args = Object.fromEntries(a.args.map(v => v.split('=')));
        const value = Number(args.value);
        if (!args.name || !Number.isFinite(value)) throw new Error('Invalid original SetMessage');
        if (firstTime) this.messages.set(args.name.toLowerCase(), value); complete = true;
      } else {
        commands.push({ ...a, firstTime });
        complete = this.action(a.name, a.args, { firstTime, dt, runtime: this, context });
      }
      if (complete !== true && complete !== 1) {
        if (complete === undefined || complete === null) blocked.push({ kind: 'action', name: a.name, args: a.args, line: a.line });
        break;
      }
      this.actionIndex++; this.firstTime = true;
    }
    // Reference scripts activate only if their Base conditions select a
    // different state; an unconditional reference does not blindly run Base.
    if (this.stack.length < 4) for (const ref of [...this.script.references, ...this.state.references]) {
      if (ref.conditions.length && !this.transition(ref.conditions, context, blocked)) continue;
      const script = this.scripts.get(ref.script);
      if (!script) { blocked.push({ kind: 'reference', name: ref.script }); continue; }
      const selected = this.transition(script.states.get(script.baseState).conditions, context, blocked);
      if (selected?.target && selected.target !== script.baseState) {
        this.stack.push({ script: this.script, returnState: ref.returnState ?? this.state.name });
        this.script = script; this.setState(selected.target);
        return { script: this.script.name, state: this.state.name, timer: this.timer, blocked, commands };
      }
    }
    const selected = this.transition(this.state.conditions, context, blocked);
    if (selected?.target) this.setState(selected.target);
    return { script: this.script.name, state: this.state.name, timer: this.timer, blocked, commands };
  }
}

// Action_EngageOpponent's ordinary source branch: globals.cpp supplies
// idealgoalrange=1.5, engagefiretime=2, aitol=.5. This emits a native action
// button request; it does NOT invent projectile velocity or instant damage.
export class SourceEngagement {
  constructor(args = [], { random = Math.random, viewDistance = null, blaster = false } = {}) {
    this.random = random; this.goalRange = 1.5; this.fireInterval = 2; this.fireRange = null;
    this.static = false; this.minimumOnly = false; this.offscreen = false; this.noFireInMiniCut = false; this.blaster = blaster;
    this.unsupported = []; this.ignoredParameters = [];
    let instant = false;
    for (const argument of args) {
      const raw = argument.toLowerCase(), match = /^(firerange|goalrange|fireinterval|minrange)[=\s]+(.+)$/.exec(raw);
      if (match) {
        const value = match[2] === 'viewrange' ? viewDistance : Number(match[2]);
        if (!Number.isFinite(value) || value < 0) throw new Error(`Unresolved original engagement parameter ${argument}`);
        if (match[1] === 'firerange') this.fireRange = value;
        else if (match[1] === 'fireinterval') this.fireInterval = value;
        else { this.goalRange = value; this.minimumOnly ||= match[1] === 'minrange'; }
      } else if (raw === 'static') { this.static = true; this.goalRange = 0; }
      else if (raw === 'instant') instant = true;
      else if (raw === 'offscreen') this.offscreen = true;
      else if (raw === 'no_fire_in_minicut') this.noFireInMiniCut = true;
      else if (raw === 'circle' || raw.startsWith('opponent=') || raw.startsWith('attack_override')) this.unsupported.push(argument);
      else this.ignoredParameters.push(argument); // Original parser also ignores unrecognized strings.
    }
    this.fireRange ??= this.goalRange === 0 ? 9999.9 : this.goalRange + 0.5;
    this.timer = instant ? 0 : this.sampleRandom() * this.fireInterval;
  }
  sampleRandom() { const value = this.random(); if (!Number.isFinite(value) || value < 0 || value > 1) throw new Error('Invalid engagement random source'); return value; }
  tick(dt, { distanceSquared, lineOfSight, drawn, targetEligible, partyMemberInWay, canAttack, miniCut = false } = {}) {
    if (!Number.isFinite(dt) || dt < 0 || dt > 1 || !Number.isFinite(distanceSquared) || distanceSquared < 0) throw new Error('Invalid engagement tick');
    const unknown = ['lineOfSight', 'drawn', 'targetEligible', 'partyMemberInWay', 'canAttack'].filter(name =>
      typeof ({ lineOfSight, drawn, targetEligible, partyMemberInWay, canAttack })[name] !== 'boolean');
    if (unknown.length || this.unsupported.length) return { fireRequested: false, blocked: [...unknown, ...this.unsupported.map(p => `parameter:${p}`)], movement: null, timer: this.timer };
    if (!targetEligible) return { fireRequested: false, blocked: ['target-ineligible'], movement: null, timer: this.timer };
    const goalRange = this.goalRange && (!lineOfSight || !drawn) ? 0.1 : this.goalRange;
    let movement = 'face';
    if (goalRange && this.blaster && distanceSquared < (goalRange - 0.5) ** 2) movement = 'retreat';
    else if (goalRange && distanceSquared > (goalRange + 0.5) ** 2 && !this.minimumOnly) movement = 'approach';
    const result = { fireRequested: false, blocked: [], movement, goalRange, timer: this.timer };
    if (!lineOfSight || (!drawn && !this.offscreen) || !(distanceSquared < this.fireRange ** 2)) return result;
    if (miniCut && this.noFireInMiniCut) this.timer = this.sampleRandom() * this.fireInterval + 2;
    else this.timer -= dt;
    if (this.timer <= 0) {
      this.timer = 0.5 * this.fireInterval + this.sampleRandom() * this.fireInterval;
      result.fireRequested = !partyMemberInWay && canAttack;
    }
    result.timer = this.timer;
    return result;
  }
}
